import { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { ApiError } from '@/lib/api.js';
import { getNudgeDraft, sendNudge } from '@/services/markets.service.js';

// MV-02 2.3 (Ship 2 item 3): the one-click follow-up email draft. The draft
// prefills from the market row's frozen snapshot; the broker edits and
// EXPLICITLY sends (nothing emails anyone without David's say so). A sent
// nudge resets the row's quiet clock via the nudge_sent history event.
// TWO ACTIONS ON A QUIET ROW, NEITHER PRESELECTED (187 addendum 2 §4).
//
// A quiet row's follow-up offered one verb: chase them. For a carrier that
// declines by not offering terms, drafting a chase asking for terms they have
// decided not to give spends the one thing a nudge spends, which is David's
// attention. So the sheet offers the other honest reading of silence beside it:
// record the decline.
//
// UNCONDITIONAL, AND ON EVERY QUIET ROW. There is deliberately no carrier trait
// gating this. The evidence could not carry one: `not_stated` is 100% for six
// carriers (Global 4, Starr 2, USAIG/Beacon/Old Republic/AIG 1 each) and IAT is
// the lone outlier at 7 for 7 WITH a reason, so "declines by silence" was a
// claim about one carrier drawn from a table that says it about six, four of
// them on a single row. Offering both verbs is neutral on any row, needs no
// evidence base and no column, and recording a decline already requires David
// to choose it and supply a reason, so nothing is presumed either way.
//
// THE SYSTEM NEVER CONVERTS SILENCE INTO A DECLINE. It offers; he decides.
export default function NudgeSheet({ marketId, open, onOpenChange, onSent, onRecordDecline }) {
  const [draft, setDraft] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !marketId) return;
    setDraft(null);
    setLoadError('');
    getNudgeDraft(marketId)
      .then((d) => {
        setDraft(d.draft);
        setSubject(d.draft.subject || '');
        setBody(d.draft.body || '');
      })
      .catch((err) => setLoadError(err instanceof ApiError ? (err.body?.error || err.code) : err.message));
  }, [open, marketId]);

  const canSend = !sending && !!draft && subject.trim().length > 0 && body.trim().length > 0 && (draft.recipients || []).length > 0;

  async function handleSend() {
    setSending(true);
    try {
      const res = await sendNudge(marketId, { subject: subject.trim(), body: body.trim() });
      if (res.failed?.length) {
        toast.error(`Sent ${res.sent.length}, failed ${res.failed.length}: ${res.failed.map((f) => f.email).join(', ')}`);
      } else {
        toast.success(`Follow-up sent to ${res.sent.length} recipient${res.sent.length === 1 ? '' : 's'}`);
      }
      for (const w of (Array.isArray(res?.warnings) ? res.warnings : [])) toast.info(w);
      onSent?.();
      if (!res.failed?.length) onOpenChange?.(false);
    } catch (err) {
      const errBody = err instanceof ApiError ? err.body : null;
      toast.error(errBody?.error || err.message || 'Send failed');
    } finally {
      setSending(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="mx-auto max-h-[92vh] overflow-y-auto md:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Follow up with the market</SheetTitle>
          <SheetDescription>
            {onRecordDecline
              ? "This row has gone quiet. Chase them, or record the decline — silence can mean either, and only you can read which."
              : "Edit the draft, then send. Sending resets this row's quiet clock."}
          </SheetDescription>
        </SheetHeader>

        {loadError ? (
          <p className="mt-6 text-sm text-danger">{loadError}</p>
        ) : !draft ? (
          <p className="mt-6 text-sm text-text-muted">Drafting from the dispatch snapshot…</p>
        ) : (
          <div className="mt-4 space-y-3">
            <div>
              <p className="mb-1 text-xs font-medium text-text-muted">To</p>
              <p className="text-sm text-text">
                {(draft.recipients || []).map((r) => r.name || r.email).join(', ') || 'No recipients on this row'}
              </p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-text-muted">Subject</p>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text"
              />
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-text-muted">Message</p>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={6}
                className="w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text"
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button onClick={handleSend} disabled={!canSend} className="flex-1">
                {sending ? 'Sending…' : `Send follow-up to ${(draft.recipients || []).length} recipient${(draft.recipients || []).length === 1 ? '' : 's'}`}
              </Button>
              {/* SIDE BY SIDE, NEITHER PRESELECTED. Secondary styling reflects
                  that chasing is the more common move, not that declining is
                  the lesser answer. It hands off to the same DeclineDialog
                  every market row uses, which still demands a reason. */}
              {onRecordDecline ? (
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { onOpenChange?.(false); onRecordDecline(marketId); }}
                >
                  They declined — record it
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
